import { HttpErrorResponse, HttpEventType, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, finalize, tap, throwError } from 'rxjs';
import { ClientObservabilityService, SKIP_CLIENT_OBSERVABILITY } from './client-observability.service';

export const httpObservabilityInterceptor: HttpInterceptorFn = (request, next) => {
  const telemetry = inject(ClientObservabilityService);
  if (
    request.context.get(SKIP_CLIENT_OBSERVABILITY)
    || telemetry.isTelemetryRequest(request.url)
    || !telemetry.shouldObserveRequest(request.url)
  ) {
    return next(request);
  }

  const startedAt = performance.now();
  const requestId = telemetry.newRequestId();
  let terminal = false;
  let slowReported = false;
  const route = telemetry.safeRoute(request.url);
  const method = request.method.toUpperCase();
  const slowTimer = window.setTimeout(() => {
    slowReported = true;
    telemetry.report({
      name: 'http.slow',
      outcome: 'timeout',
      error_type: 'client_slow_request',
      duration_ms: telemetry.slowRequestMs,
      route,
      request_id: requestId,
      details: { method },
    });
  }, telemetry.slowRequestMs);
  const observed = request.clone({ setHeaders: telemetry.headers(requestId) });

  return next(observed).pipe(
    tap((event) => {
      if (event.type !== HttpEventType.Response || !(event instanceof HttpResponse)) return;
      terminal = true;
      telemetry.report({
        name: 'http.completed',
        outcome: 'success',
        duration_ms: Math.max(0, Math.round(performance.now() - startedAt)),
        route,
        request_id: event.headers.get('X-Request-ID') ?? requestId,
        trace_id: event.headers.get('X-Trace-ID') ?? undefined,
        details: {
          method,
          status: String(event.status),
          ...(slowReported ? { state: 'recovered' } : {}),
        },
      });
    }),
    catchError((error: HttpErrorResponse) => {
      terminal = true;
      telemetry.reportHttpFailure(request.url, method, startedAt, error, requestId);
      return throwError(() => error);
    }),
    finalize(() => {
      window.clearTimeout(slowTimer);
      if (!terminal) {
        telemetry.report({
          name: 'http.cancelled',
          outcome: 'cancelled',
          error_type: 'client_cancelled',
          duration_ms: Math.max(0, Math.round(performance.now() - startedAt)),
          route,
          request_id: requestId,
          details: { method },
        });
      }
    }),
  );
};
